/**
 * @file Résout l'exception de planning restaurant applicable à un jour établissement.
 * @module common.utils.restaurant.exceptionDayResolver
 *
 * Source unique de la règle « quelle exception s'applique à ce jour » pour toutes
 * les surfaces (dashboard, widget, site public). Le principe est de ne jamais
 * comparer des instants : on ne manipule que des clés de jour `YYYY-MM-DD`.
 *
 * Les dates d'exception arrivent déjà exprimées dans le fuseau de l'établissement :
 * chaînes murales `YYYY-MM-DD HH:mm:ss` du middleware de conversion (dashboard) ou
 * jours purs `YYYY-MM-DD` sérialisés par le backend (widget, site public). Prendre
 * le préfixe jour de ces chaînes est donc exact, sans construire de Date, ce qui
 * évite toute réinterprétation UTC et les formats mal lus par Safari.
 *
 * Seuls les objets Date (grille de calendrier, pickers des réglages) et les instants
 * ISO avec fuseau (`{$date}` d'exports Mongo) passent par les champs locaux de la Date,
 * qui représentent l'heure murale de l'établissement dans un navigateur sur site.
 *
 * Priorité, alignée sur `ConfigurationService.findMatchingException` côté backend :
 * date unique, puis plage, puis récurrence hebdomadaire.
 */

import {ensureDate, formatDateWithoutTimezone} from '../dateTimeUtils';
import {WEEK_DAYS} from '../moments';

/**
 * Chaîne « naïve » : jour seul ou jour + heure murale, sans indicateur de fuseau.
 * Le premier groupe capture la clé de jour.
 */
const NAIVE_DAY_PATTERN = /^(\d{4}-\d{2}-\d{2})(?:$|[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$)/;

/**
 * Types d'exception à date unique (`single` est l'alias historique de `unique`).
 */
const SINGLE_DAY_TYPES = new Set(['unique', 'single']);

/**
 * Déballe l'enveloppe MongoDB étendue `{$date: ...}` si présente.
 *
 * @param {*} value - Valeur brute
 * @return {*} Valeur utile
 */
const unwrapMongoDate = (value) =>
  (value && typeof value === 'object' && !(value instanceof Date) && value.$date ? value.$date : value);

/**
 * Normalise une valeur de date en clé de jour établissement `YYYY-MM-DD`.
 *
 * Accepte un objet Date, une chaîne naïve (`YYYY-MM-DD` ou `YYYY-MM-DD HH:mm:ss`),
 * un instant ISO avec fuseau, un timestamp ou l'enveloppe `{$date}`.
 *
 * @param {*} value - Valeur de date issue d'une exception, d'un picker ou d'une grille
 * @return {string|null} Clé de jour, ou null si indéterminable
 */
export function toDayKey(value) {
  const raw = unwrapMongoDate(value);
  if (!raw) return null;

  if (typeof raw === 'string') {
    const naive = raw.match(NAIVE_DAY_PATTERN);
    if (naive) return naive[1];
  }

  return formatDateWithoutTimezone(ensureDate(raw));
}

/**
 * Reconstruit une Date locale à minuit pour une clé de jour (usage affichage).
 *
 * @param {string} dayKey - Clé de jour `YYYY-MM-DD`
 * @return {Date|null} Date locale à minuit, ou null si la clé est invalide
 */
export function dayKeyToDate(dayKey) {
  return dayKey ? ensureDate(dayKey) : null;
}

/**
 * Clé anglaise du jour de semaine (`monday`…`sunday`) d'une clé de jour.
 *
 * @param {string} dayKey - Clé de jour `YYYY-MM-DD`
 * @return {string|null} Clé de `WEEK_DAYS`, ou null si la clé est invalide
 */
export function weekdayKeyForDay(dayKey) {
  const date = dayKeyToDate(dayKey);
  return date ? WEEK_DAYS[(date.getDay() + 6) % 7] : null;
}

/**
 * Bornes de jour d'une exception à plage, en clés `YYYY-MM-DD`.
 *
 * @param {Object} exception - Exception de type `range`
 * @return {{start: string, end: string}|null} Bornes, ou null si incomplètes
 */
export function rangeDayKeys(exception) {
  if (!exception?.range) return null;
  const start = toDayKey(exception.range.start);
  const end = toDayKey(exception.range.end);
  return start && end ? {start, end} : null;
}

/**
 * Indique si une exception couvre un jour donné, quel que soit son type.
 *
 * @param {Object} exception - Exception de la configuration
 * @param {string} dayKey - Clé de jour `YYYY-MM-DD`
 * @return {boolean} Vrai si l'exception s'applique ce jour-là
 */
export function exceptionCoversDay(exception, dayKey) {
  if (!exception || !dayKey) return false;

  if (SINGLE_DAY_TYPES.has(exception.type)) {
    return Boolean(exception.date) && toDayKey(exception.date) === dayKey;
  }

  if (exception.type === 'range') {
    const bounds = rangeDayKeys(exception);
    return Boolean(bounds) && dayKey >= bounds.start && dayKey <= bounds.end;
  }

  if (exception.type === 'recurring') {
    return Boolean(exception.dayOfWeek) && exception.dayOfWeek === weekdayKeyForDay(dayKey);
  }

  return false;
}

/**
 * Trouve l'exception applicable à un jour : date unique, puis plage, puis récurrence.
 *
 * @param {Array<Object>} exceptions - Exceptions de la configuration
 * @param {Date|string|Object} day - Jour visé (Date locale, clé de jour ou chaîne murale)
 * @return {Object|null} Exception applicable, ou null
 */
export function findExceptionForDay(exceptions, day) {
  const dayKey = toDayKey(day);
  if (!dayKey || !Array.isArray(exceptions) || exceptions.length === 0) return null;

  const candidates = exceptions.filter(Boolean);
  const byType = (predicate) => candidates.find((exception) => predicate(exception) && exceptionCoversDay(exception, dayKey));

  return (
    byType((exception) => SINGLE_DAY_TYPES.has(exception.type)) ||
    byType((exception) => exception.type === 'range') ||
    byType((exception) => exception.type === 'recurring') ||
    null
  );
}
